import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const QUERY_KEY = ['walletNotificationCount'];

/**
 * Subscribes to wallet-notification updates over ActionCable and exposes the
 * current notification count for the signed-in actor.
 *
 * - Fetches the initial count from the API the first time it mounts.
 * - Listens for `wallet_notification_count_changed` events and updates state.
 * - Works for both kid and guardian: the API URL is mode-specific, but the
 *   ActionCable channel name is the same; the server picks the right stream
 *   based on the connection identity.
 *
 * The query key is shared (`['walletNotificationCount']`), so wallet pages
 * that reset the counter server-side can invalidate this query after their
 * own data fetch completes to avoid races between the initial GET and the
 * reset broadcast.
 */
export const useWalletNotificationCount = ({ apiUrl }) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await fetch(apiUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return { count: 0 };
      return response.json();
    },
    staleTime: 0,
    refetchOnWindowFocus: false
  });

  // ActionCable subscription writes count straight into the query cache so all
  // consumers re-render in sync.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.App || !window.App.cable) {
      return undefined;
    }

    const subscription = window.App.cable.subscriptions.create(
      { channel: 'WalletNotificationChannel' },
      {
        received(payload) {
          if (payload && payload.type === 'wallet_notification_count_changed') {
            queryClient.setQueryData(QUERY_KEY, {
              count: payload.notification_count || 0
            });
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  return { count: data?.count || 0 };
};

export const WALLET_NOTIFICATION_COUNT_QUERY_KEY = QUERY_KEY;

export default useWalletNotificationCount;
